import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchInvoiceApi,
  fetchGroupedInvoicesApi,
  fetchInvoicesByAgentApi,
  addInvoiceApi,
  updateInvoiceApi,
  deleteInvoiceApi,
} from "../services/invoiceService";

// Fetch all invoices (new)
export const fetchInvoice = createAsyncThunk(
  "invoice/fetchInvoice",
  async () => {
    const response = await fetchInvoiceApi();
    console.log("response all invoices", response);
    return response;
  }
);

// Fetch grouped invoices by agent
export const fetchGroupedInvoices = createAsyncThunk(
  "invoice/fetchGroupedInvoices",
  async () => {
    const response = await fetchGroupedInvoicesApi();
    console.log("response grouped", response);
    return response;
  }
);

// Fetch invoices by agent
export const fetchInvoicesByAgent = createAsyncThunk(
  "invoice/fetchInvoicesByAgent",
  async (agentName) => {
    const response = await fetchInvoicesByAgentApi(agentName);
    console.log("response by agent", response);
    return { agentName, invoices: response };
  }
);

// Add new invoice
export const addInvoice = createAsyncThunk(
  "invoice/addInvoice",
  async (invoiceData, { rejectWithValue }) => {
    try {
      console.log("invoiceData:", invoiceData);
      const response = await addInvoiceApi(invoiceData);
      console.log("API Response on Add invoice:", response);
      return Array.isArray(response) ? response : [response]; // Ensure array for consistency
    } catch (error) {
      console.error("Error adding invoice:", error);
      return rejectWithValue(error.response?.data || "Failed to add invoice");
    }
  }
);

export const updateInvoice = createAsyncThunk(
  "invoice/updateInvoice",
  async (updatedInvoice, { rejectWithValue }) => {
    try {
      console.log("updateInvoice", updatedInvoice);
      const response = await updateInvoiceApi(updatedInvoice);
      return Array.isArray(response) ? response[0] : response; // Ensure single object
    } catch (error) {
      console.error("Error updating invoice:", error);
      return rejectWithValue(
        error.response?.data || "Failed to update invoice"
      );
    }
  }
);

// Delete invoice by ID
export const deleteInvoice = createAsyncThunk(
  "invoice/deleteInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteInvoiceApi(id);
      return { id };
    } catch (error) {
      console.error("Error deleting invoice:", error);
      return rejectWithValue(
        error.response?.data || "Failed to delete invoice"
      );
    }
  }
);

const invoiceSlice = createSlice({
  name: "invoice",
  initialState: {
    invoices: [],
    groupedInvoices: [],
    agentInvoices: [],
    currentAgent: null,
    status: "idle",
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchInvoice cases
      .addCase(fetchInvoice.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.invoices = action.payload || [];
      })
      .addCase(fetchInvoice.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        state.invoices = [];
      })
      .addCase(fetchGroupedInvoices.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchGroupedInvoices.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.groupedInvoices = action.payload || [];
      })
      .addCase(fetchGroupedInvoices.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        state.groupedInvoices = [];
      })
      .addCase(fetchInvoicesByAgent.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchInvoicesByAgent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.agentInvoices = action.payload.invoices || [];
        state.currentAgent = action.payload.agentName;
      })
      .addCase(fetchInvoicesByAgent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        state.agentInvoices = [];
      })
      .addCase(addInvoice.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addInvoice.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload && action.payload.length > 0) {
          state.invoices.push(...action.payload);
        }
      })
      .addCase(addInvoice.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(updateInvoice.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedInvoice = action.payload;
        if (!updatedInvoice || !updatedInvoice.id) return;

        // Update agentInvoices
        const agentIndex = state.agentInvoices.findIndex(
          (invoice) => invoice.id === updatedInvoice.id
        );
        if (agentIndex !== -1 && Array.isArray(state.agentInvoices)) {
          state.agentInvoices = [
            ...state.agentInvoices.slice(0, agentIndex),
            { ...updatedInvoice },
            ...state.agentInvoices.slice(agentIndex + 1),
          ];
        }

        // Update all invoices
        const allIndex = state.invoices.findIndex(
          (inv) => inv.id === updatedInvoice.id
        );
        if (allIndex !== -1) {
          state.invoices = [
            ...state.invoices.slice(0, allIndex),
            { ...updatedInvoice },
            ...state.invoices.slice(allIndex + 1),
          ];
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteInvoice.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { id } = action.payload;

        // Filter agentInvoices
        if (Array.isArray(state.agentInvoices)) {
          state.agentInvoices = state.agentInvoices.filter(
            (invoice) => invoice.id !== id
          );
        }

        // Filter all invoices
        state.invoices = state.invoices.filter((invoice) => invoice.id !== id);
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearError } = invoiceSlice.actions;
export default invoiceSlice.reducer;
